import React, { useState, useMemo } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { ReservationHistory } from '../../services/types';
import ReservationCard from '../reservations/ReservationCard';

interface HistoryListViewProps {
    reservations: ReservationHistory[];
    houses?: any[]; // Optional, only used as fallback
    onSelectReservation: (reservation: ReservationHistory) => void;
}

const HistoryListView: React.FC<HistoryListViewProps> = ({ reservations, houses = [], onSelectReservation }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const filteredReservations = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        return reservations.filter((reservation) => {
            const checkIn = parseISO(reservation.check_in_date);
            const checkOut = parseISO(reservation.check_out_date);
            return checkIn <= monthEnd && checkOut >= monthStart;
        });
    }, [reservations, currentMonth]);

    const getHouseName = (reservation: ReservationHistory) => {
        // Use house_name from the reservation history if available
        if (reservation.house_name) {
            return reservation.house_name;
        }
        // Fallback to looking up in houses array
        if (reservation.house_id) {
            const house = houses.find((h) => h.public_id === reservation.house_id);
            return house ? (house.name || house.address) : 'Unknown House';
        }
        return 'Unknown House';
    };

    const handlePrevMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
        );
    };

    const handleNextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        );
    };

    const handleToday = () => {
        setCurrentMonth(new Date());
    };

    return (
        <div>
            {/* Month Navigator */}
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handlePrevMonth}
                >
                    <ChevronLeft size={18} />
                </Button>

                <div className="d-flex gap-3 align-items-center">
                    <h5 className="mb-0">{format(currentMonth, 'MMMM yyyy')}</h5>
                    <Button variant="outline-secondary" size="sm" onClick={handleToday}>
                        Today
                    </Button>
                </div>

                <Button variant="outline-primary" size="sm" onClick={handleNextMonth}>
                    <ChevronRight size={18} />
                </Button>
            </div>

            {/* Reservations List */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentMonth.toISOString()}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    {filteredReservations.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <p>No reservations for this month</p>
                        </div>
                    ) : (
                        <Row className="g-3">
                            {filteredReservations.map((reservation) => (
                                <Col key={reservation.public_id} xs={12} md={6} lg={4}>
                                    <ReservationCard
                                        reservation={reservation as any}
                                        houseName={getHouseName(reservation)}
                                        onClick={() => onSelectReservation(reservation)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Summary */}
            <div className="mt-4 p-3 bg-light rounded text-center">
                <strong>{filteredReservations.length}</strong> reservation
                {filteredReservations.length !== 1 ? 's' : ''} in {format(currentMonth, 'MMMM yyyy')}
            </div>
        </div>
    );
};

export default HistoryListView;